import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useDrawingStore } from '../../stores/drawingStore';
import { Point, Element, ElementType } from '../../types/drawing';
import { createElement } from '../../utils/canvasUtils';

interface CanvasProps {
  className?: string;
}

export const Canvas: React.FC<CanvasProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const {
    currentTool,
    strokeWidth,
    strokeColor,
    fillColor,
    fillOpacity,
    currentLayer,
    addElement,
    elements,
  } = useDrawingStore();

  // Initialize canvas
  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        setError('Canvas ref not found');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setError('Failed to get 2D context');
        return;
      }

      // Set canvas size to viewport
      const resizeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        
        // Set default styles
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
        ctx.fillStyle = fillColor;
        
        // Draw a simple background to show canvas is working
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw a simple test pattern
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1;
        for (let i = 0; i < canvas.width; i += 50) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, canvas.height);
          ctx.stroke();
        }
        for (let i = 0; i < canvas.height; i += 50) {
          ctx.beginPath();
          ctx.moveTo(0, i);
          ctx.lineTo(canvas.height, i);
          ctx.stroke();
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => window.removeEventListener('resize', resizeCanvas);
    } catch (err) {
      setError(`Canvas initialization error: ${err}`);
    }
  }, [strokeColor, strokeWidth, fillColor]);

  // Redraw all elements and current path when they change

  // Draw a single element
  const drawElement = useCallback((ctx: CanvasRenderingContext2D, element: Element) => {
    try {
      ctx.save();
      
      // Set styles
      ctx.strokeStyle = element.strokeColor;
      ctx.lineWidth = element.strokeWidth;
      ctx.fillStyle = element.fillColor;
      ctx.globalAlpha = element.fillOpacity;

      switch (element.type) {
        case 'pencil':
          if (element.points.length < 2) return;
          
          ctx.beginPath();
          ctx.moveTo(element.points[0].x, element.points[0].y);
          
          for (let i = 1; i < element.points.length; i++) {
            ctx.lineTo(element.points[i].x, element.points[i].y);
          }
          
          ctx.stroke();
          break;

        case 'line':
          if (element.points.length < 2) return;
          
          ctx.beginPath();
          ctx.moveTo(element.points[0].x, element.points[0].y);
          ctx.lineTo(element.points[1].x, element.points[1].y);
          ctx.stroke();
          break;

        case 'rectangle': {
          if (element.points.length < 2) return;
          const rect = getBoundingRect(element.points[0], element.points[1]);
          if (element.fillOpacity > 0) {
            ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
          }
          ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
          }
          break;

        case 'circle': {
          if (element.points.length < 2) return;
          const center = element.points[0];
          const radius = getDistance(center, element.points[1]);
          ctx.beginPath();
          ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
          if (element.fillOpacity > 0) {
            ctx.fill();
          }
          ctx.stroke();
          }
          break;
      }
      
      ctx.restore();
    } catch (err) {
      setError(`Element drawing error: ${err}`);
    }
  }, []);

  // Draw preview path in real-time
  const drawPreviewPath = useCallback((ctx: CanvasRenderingContext2D, path: Point[]) => {
    if (path.length < 2) return;
    
    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i].x, path[i].y);
    }
    
    ctx.stroke();
    ctx.restore();
  }, [strokeColor, strokeWidth]);

  // Redraw all elements and current path when they change
  useEffect(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw background
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      for (let i = 0; i < canvas.width; i += 50) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for (let i = 0; i < canvas.height; i += 50) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }

      // Draw all saved elements
      elements.forEach((element) => {
        drawElement(ctx, element);
      });

      // Draw current path in real-time (preview)
      if (isDrawing && currentPath.length > 1) {
        drawPreviewPath(ctx, currentPath);
      }
    } catch (err) {
      setError(`Drawing error: ${err}`);
    }
  }, [elements, currentPath, isDrawing, drawElement, drawPreviewPath]);
  // Get bounding rectangle for rectangle tool
  const getBoundingRect = (p1: Point, p2: Point) => {
    return {
      x: Math.min(p1.x, p2.x),
      y: Math.min(p1.y, p2.y),
      width: Math.abs(p2.x - p1.x),
      height: Math.abs(p2.y - p1.y),
    };
  };

  // Get distance between two points
  const getDistance = (p1: Point, p2: Point) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Get mouse position relative to canvas
  const getMousePos = useCallback((event: MouseEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  // Handle mouse down
  const handleMouseDown = useCallback((event: MouseEvent) => {
    if (currentTool === 'eraser') return;
    
    const pos = getMousePos(event);
    setIsDrawing(true);
    setCurrentPath([pos]);
  }, [currentTool, getMousePos]);

  // Handle mouse move
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDrawing || currentTool === 'eraser') return;
    
    const pos = getMousePos(event);
    setCurrentPath(prev => [...prev, pos]);
  }, [isDrawing, currentTool, getMousePos]);

  // Handle mouse up
  const handleMouseUp = useCallback((event: MouseEvent) => {
    if (!isDrawing || currentTool === 'eraser') return;
    
    const pos = getMousePos(event);
    const finalPath = [...currentPath, pos];
    
    if (finalPath.length >= 2) {
      // Only create elements for drawing tools
      const drawingTools: Set<ElementType> = new Set(['pencil','line','arrow','rectangle','circle','text']);
      if (!drawingTools.has(currentTool as ElementType)) {
        setIsDrawing(false);
        setCurrentPath([]);
        return;
      }
      const element = createElement(currentTool as ElementType, finalPath, {
        strokeWidth,
        strokeColor,
        fillColor,
        fillOpacity,
        layerId: currentLayer,
      });
      
      addElement(element);
    }
    
    setIsDrawing(false);
    setCurrentPath([]);
  }, [isDrawing, currentTool, currentPath, strokeWidth, strokeColor, fillColor, fillOpacity, currentLayer, addElement, getMousePos]);

  // Setup event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-50">
        <div className="text-red-600 text-center">
          <h3 className="text-lg font-semibold mb-2">Canvas Error</h3>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full relative ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-crosshair block"
        style={{ touchAction: 'none' }}
      />
    </div>
  );
};
